'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useLoginPromptStore } from '@/store/login-prompt-store';
import { useOptionalLoginModals } from '@/components/Login/common/LoginModalsManager';
import { useAuthUser } from '@/lib/auth/common/useSession';

const SKIPPABLE_DELAY_MS = 500;

interface UseLoginPromptOptions {
  /** Show prompt automatically when entering this course (on every new route/course) */
  autoTrack?: boolean;
  /** Course identifier for per-course skip count (e.g. courseId or course uid) */
  courseId?: string;
  /** Callback when user is blocked (required modal shown) */
  onBlock?: () => void;
}

interface UseLoginPromptReturn {
  isPromptOpen: boolean;
  promptType: 'skippable' | 'required' | null;
  isLoggedIn: boolean;
  /** Block content when required modal is shown and user is not logged in */
  shouldBlockContent: boolean;
  dismissPrompt: () => void;
  handleLogin: () => void;
  resetTracking: () => void;
}

export function useLoginPrompt(options: UseLoginPromptOptions = {}): UseLoginPromptReturn {
  const { autoTrack = true, courseId, onBlock } = options;

  const {
    isPromptOpen,
    promptType,
    openPrompt,
    closePrompt,
    resetAll,
    getSkipCount,
    incrementSkipCount,
    shouldShowRequired,
  } = useLoginPromptStore();

  const loginModals = useOptionalLoginModals();

  // Must stay reactive (see useAuthUser()'s doc comment). isLoading matters as much as
  // isLoggedIn: it's true for the whole session round trip, during which isLoggedIn is
  // false even for a signed-in user — acting on that gap showed the undismissable
  // 'required' prompt to logged-in users.
  const { isLoggedIn, isLoading: isAuthLoading } = useAuthUser();
  const hasShownForCourseRef = useRef<string | null>(null);
  const delayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Live mirror for the delayed callback below, whose closure otherwise holds a
  // 500ms-stale isLoggedIn since the timeout is scheduled once and never rescheduled.
  const isLoggedInRef = useRef(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;

  const shouldBlockContent = !isLoggedIn && promptType === 'required';

  // Skippable until closed 3x for this course, then required.
  const showPromptForCourse = useCallback(() => {
    if (!courseId || isLoggedIn || isAuthLoading) return;
    if (hasShownForCourseRef.current === courseId) return;
    hasShownForCourseRef.current = courseId;

    const type = shouldShowRequired(courseId) ? 'required' : 'skippable';
    if (type === 'required') {
      openPrompt('required');
      onBlock?.();
    } else {
      delayTimeoutRef.current = setTimeout(() => {
        if (!isLoggedInRef.current) {
          openPrompt('skippable');
        }
      }, SKIPPABLE_DELAY_MS);
    }
  }, [courseId, isLoggedIn, isAuthLoading, shouldShowRequired, openPrompt, onBlock]);

  useEffect(() => {
    if (autoTrack && courseId && !isLoggedIn && !isAuthLoading) {
      showPromptForCourse();
    }
    return () => {
      if (delayTimeoutRef.current) {
        clearTimeout(delayTimeoutRef.current);
      }
    };
  }, [autoTrack, courseId, isLoggedIn, isAuthLoading, showPromptForCourse]);

  // Closes a stuck 'required' prompt for in-place logins (registration, partner SSO)
  // that never call resetLoginPrompt(); reload-based logins don't need this.
  useEffect(() => {
    if (isLoggedIn && isPromptOpen) closePrompt();
  }, [isLoggedIn, isPromptOpen, closePrompt]);

  // New course route: reset "has shown" so the prompt can show again.
  useEffect(() => {
    hasShownForCourseRef.current = null;
  }, [courseId]);

  const dismissPrompt = useCallback(() => {
    if (promptType === 'skippable' && courseId) {
      incrementSkipCount(courseId);
      closePrompt();
    }
  }, [promptType, courseId, incrementSkipCount, closePrompt]);

  const handleLogin = useCallback(() => {
    closePrompt();
    setTimeout(() => {
      if (loginModals) loginModals.openLoginModal();
    }, 100);
  }, [closePrompt, loginModals]);

  const resetTracking = useCallback(() => {
    resetAll();
  }, [resetAll]);

  return {
    isPromptOpen,
    promptType,
    isLoggedIn,
    shouldBlockContent,
    dismissPrompt,
    handleLogin,
    resetTracking,
  };
}
